// IncomingTab — table of open POs grouped by SKU, plus the existing
// inline "Mark as Received" quick action and the new IncomingDrawer.
//
// All receipt writes (inline and drawer) flow through writeReceipt in
// stockWrites.js so the new ledger and the legacy single-shot columns
// stay in sync during the soft cutover.

import { useMemo, useRef, useState } from 'react';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { fileToBase64 } from '../lib/fileEncoding';
import { writeReceipt } from './stockWrites';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import IncomingDrawer from './IncomingDrawer';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const ACCEPTED_PS_MIME = ['application/pdf', 'image/png', 'image/jpeg'];

function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
function daysTo(d) { if (!d) return null; return Math.floor((new Date(d) - new Date()) / 86400000); }

// Delivery status — Ace's Complete / With Excess / Lacking enum
// computed from qty (PO expected) vs delivered_qty (physical receipt).
// Stored as null until first receipt, so a fresh PO row reads
// "pending"; "disputed" overrides everything when disputed_at is set.
function computeIncomingStatus(r) {
  if (r.disputed_at) return 'disputed';
  if (r.closed_at)   return 'short_received';   // explicit final/short close
  const expected = Number(r.qty || 0);
  const recv     = Number(r.received_qty || 0);
  // Phase 2.1: receipt-progress takes priority once any receipt exists.
  if (recv > 0) return (expected > 0 && recv >= expected) ? 'complete' : 'partial';
  // No receipts yet — fall back to Ace's delivered-qty variance enum.
  if (r.delivered_qty == null) return 'pending';
  const delivered = Number(r.delivered_qty);
  if (expected === 0) return 'pending';
  if (delivered === expected) return 'complete';
  if (delivered >  expected)  return 'excess';
  return 'lacking';
}
const STATUS_PILL = {
  pending:        { label: 'Pending',        bg: '#F1F1ED', fg: '#6E7278' },
  partial:        { label: 'Partial',        bg: '#FEF3C7', fg: '#92400E' },
  complete:       { label: 'Complete',       bg: '#E2F4EA', fg: '#036E43' },
  excess:         { label: 'Excess',         bg: '#FFF1D6', fg: '#A56B00' },
  lacking:        { label: 'Lacking',        bg: '#FEE5DA', fg: '#9A4820' },
  short_received: { label: 'Short-received', bg: '#FEE5DA', fg: '#9A4820' },
  disputed:       { label: 'Disputed',       bg: '#EFE6F8', fg: '#6B4E8C' },
};

// Phase 2.1 receipt-progress summary for a row: "Received X / Y" + remaining.
function receiptProgress(r) {
  const qty  = Number(r.qty || 0);
  const recv = Number(r.received_qty || 0);
  return { qty, recv, remaining: Math.max(0, qty - recv), isPartial: recv > 0 && recv < qty };
}

// Read-only "PAID" badge for the Incoming list. Reflects the stock-side
// quick-reference flag (forecaster_incoming_stock.paid) — NOT the Finance
// invoice payment status. Toggled from the IncomingDrawer.
function PaidPill() {
  return (
    <span
      title="Paid — reference flag only, not Finance payment status"
      style={{
        display: 'inline-block', padding: '2px 9px', borderRadius: 999,
        fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em',
        background: '#E3F2EC', color: '#0F8E6F',
      }}
    >PAID</span>
  );
}

export default function IncomingTab({ session, catalog, skus, incomingData, loadIncoming, loadSkus }) {
  const canWrite = session?.role === 'admin' || session?.permissions?.stock_write !== false;
  const [addOpen, setAddOpen] = useState(false);
  const [prevOpen, setPrevOpen] = useState(false);
  // viewMode: 'sku' groups by SKU (default — answers "what's coming for
  // this SKU"). 'po' groups by PO ref with a rollup header strip showing
  // PO Qty / PS Qty / Delivered / Outstanding — Ace's PO Tracker parity
  // ask for Stock-Incoming.
  const [viewMode, setViewMode] = useState('sku');
  const [showFinalized, setShowFinalized] = useState(false); // Phase 2.1: reveal short-closed rows so they can be reopened
  const [newRow, setNewRow] = useState({
    sku_id: '', qty: '', expected_date: '', supplier: '', po_ref: '',
    batch_number: '', expiry_date: '', warehouse_location: '', skutopia_transfer_id: '',
    packing_slip_number: '', packing_slip_qty: '',
  });
  const [drawerPo, setDrawerPo] = useState(null);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receiveQty, setReceiveQty] = useState('');
  const [receiveComment, setReceiveComment] = useState('');
  const [receiving, setReceiving] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);
  // Packing-slip auto-extract — Phase 2 of Ace's PO Tracker parity ask.
  // Drops a PDF/image into the Add Incoming modal; calls extract-po
  // Lambda with kind='packing_slip' (one Lambda, two prompts).
  const [psExtracting, setPsExtracting] = useState(false);
  const [psExtractError, setPsExtractError] = useState('');
  const [psPendingFile, setPsPendingFile] = useState(null);
  const [psAutoFilled, setPsAutoFilled] = useState({});
  const psFileRef = useRef(null);

  // Open POs joined to their SKU + catalog.
  const rows = useMemo(() => {
    return [...(incomingData || [])]
      // Phase 2.1: keep partial receipts visible. Show a row if it is
      // disputed, OR it is not fully received (received_qty < qty) AND not
      // explicitly closed-short (closed_at IS NULL). Hides only fully
      // received (auto) or short-closed rows. (Was: `!r.received_at`,
      // which wrongly hid the row after ANY receipt.)
      .filter(r => {
        const qty  = Number(r.qty || 0);
        const recv = Number(r.received_qty || 0);
        const fullyReceived = qty > 0 && recv >= qty;
        if (r.disputed_at) return true;                          // disputes always show until resolved
        if (!fullyReceived && r.closed_at == null) return true;  // open / partial-more-coming
        if (showFinalized && r.closed_at != null) return true;   // closed-short — only when "Show finalized" is ON (to reopen)
        return false;                                            // fully-received stays hidden (by design)
      })
      .map(r => {
        const sku = skus.find(s => s.id === r.sku_id);
        const cat = catalog.find(c => c.id === sku?.catalog_id);
        return { ...r, _sku: sku, _cat: cat, _daysTo: daysTo(r.expected_date) };
      });
  }, [incomingData, skus, catalog, showFinalized]);

  const today = new Date().toISOString().slice(0, 10);
  const upcomingRows = useMemo(() => rows.filter(r => (r.expected_date || '') >= today), [rows, today]);
  const previousRows = useMemo(() => rows.filter(r => (r.expected_date || '') < today).sort((a, b) => (b.expected_date || '').localeCompare(a.expected_date || '')), [rows, today]);

  function buildGroups(rowList) {
    const m = new Map();
    for (const r of rowList) {
      const key = r._sku?.id || r.sku_id;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    for (const list of m.values()) list.sort((a, b) => (a.expected_date || '').localeCompare(b.expected_date || ''));
    return Array.from(m.entries())
      .map(([skuId, list]) => ({ skuId, list, earliest: list[0]?.expected_date || '' }))
      .sort((a, b) => a.earliest.localeCompare(b.earliest));
  }

  function buildPoGroups(rowList) {
    const m = new Map();
    for (const r of rowList) {
      const key = r.po_ref || '__no_po__';
      if (!m.has(key)) {
        m.set(key, {
          poRef: key === '__no_po__' ? null : key,
          list: [], supplier: null,
          totalQty: 0, totalPs: 0, totalDelivered: 0, earliest: '',
        });
      }
      const g = m.get(key);
      g.list.push(r);
      if (!g.supplier) g.supplier = r._cat?.supplier_name || r.supplier || null;
      g.totalQty += Number(r.qty || 0);
      g.totalPs += Number(r.packing_slip_qty || 0);
      g.totalDelivered += Number(r.delivered_qty || 0);
    }
    for (const g of m.values()) {
      g.list.sort((a, b) => (a.expected_date || '').localeCompare(b.expected_date || ''));
      g.earliest = g.list[0]?.expected_date || '';
      g.outstanding = Math.max(0, g.totalQty - g.totalDelivered);
      g.pctDelivered = g.totalQty > 0 ? Math.round((g.totalDelivered / g.totalQty) * 100) : 0;
    }
    return Array.from(m.values()).sort((a, b) => {
      if (!a.poRef && b.poRef) return 1;
      if (a.poRef && !b.poRef) return -1;
      if (!a.poRef && !b.poRef) return 0;
      return a.poRef.localeCompare(b.poRef, undefined, { numeric: true });
    });
  }

  // Group by SKU so multi-PO SKUs render as a parent + indented children.
  const groups = useMemo(() => buildGroups(upcomingRows), [upcomingRows]);
  const prevGroups = useMemo(() => buildGroups(previousRows).sort((a, b) => b.earliest.localeCompare(a.earliest)), [previousRows]);
  const poGroups = useMemo(() => buildPoGroups(upcomingRows), [upcomingRows]);
  const prevPoGroups = useMemo(() => buildPoGroups(previousRows), [previousRows]);

  function onSkuSelect(skuId) {
    const sku = skus.find(s => s.id === skuId);
    const cat = catalog.find(c => c.id === sku?.catalog_id);
    setNewRow(f => ({ ...f, sku_id: skuId, supplier: cat?.supplier_name || '' }));
  }

  function setNewRowAuto(updates) {
    setNewRow(f => ({ ...f, ...updates }));
    setPsAutoFilled(af => ({ ...af, ...Object.fromEntries(Object.keys(updates).map(k => [k, true])) }));
  }
  function clearAutoFill(key) {
    setPsAutoFilled(af => { const n = { ...af }; delete n[key]; return n; });
  }

  // Drop a packing-slip PDF/image → extract-po Lambda (kind='packing_slip')
  // → fields auto-fill. SKU is matched best-effort by sku_code; if no match,
  // the user picks the SKU manually.
  async function extractPackingSlip(file) {
    if (!file) return;
    if (!ACCEPTED_PS_MIME.includes(file.type)) {
      setPsExtractError(`${file.name}: only PDF, PNG, JPG accepted.`);
      return;
    }
    setPsExtracting(true);
    setPsExtractError('');
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch(import.meta.env.VITE_AWS_EXTRACT_PO_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'packing_slip',
          file_base64: base64,
          mime_type: file.type,
          userPin: session?.code,
        }),
      });
      if (!res.ok) {
        let msg = `Extract failed (HTTP ${res.status})`;
        try { const e = await res.json(); if (e?.error) msg = e.error; } catch {}
        setPsExtractError(`${file.name}: ${msg}`);
        setPsExtracting(false);
        return;
      }
      const d = await res.json();
      const updates = {};
      if (d.packing_slip_number) updates.packing_slip_number = String(d.packing_slip_number);
      if (d.po_ref) updates.po_ref = String(d.po_ref);
      if (d.supplier && !newRow.supplier) updates.supplier = String(d.supplier);
      if (d.skutopia_transfer_id) updates.skutopia_transfer_id = String(d.skutopia_transfer_id);
      if (d.expected_arrival_date) updates.expected_date = d.expected_arrival_date;
      else if (d.shipment_date) updates.expected_date = d.shipment_date;
      if (d.batch_number) updates.batch_number = String(d.batch_number);
      if (d.expiry_date) updates.expiry_date = d.expiry_date;
      if (d.warehouse_location) updates.warehouse_location = String(d.warehouse_location);
      if (d.total_qty != null) {
        updates.packing_slip_qty = String(d.total_qty);
        if (!newRow.qty) updates.qty = String(d.total_qty);
      }
      // Best-effort SKU match: first line item's sku_code against catalog.
      if (Array.isArray(d.line_items) && d.line_items.length > 0 && !newRow.sku_id) {
        const code = d.line_items[0].sku_code;
        if (code) {
          const cat = catalog.find(c => c.sku_code && c.sku_code.toLowerCase() === String(code).toLowerCase());
          if (cat) {
            const sku = skus.find(s => s.catalog_id === cat.id);
            if (sku) {
              updates.sku_id = sku.id;
              if (!updates.supplier && !newRow.supplier) updates.supplier = cat.supplier_name || '';
            }
          }
        }
      }
      const filledKeys = Object.keys(updates);
      if (filledKeys.length === 0) {
        setPsExtractError(`${file.name}: extracted but no packing-slip fields recognised. Fill manually.`);
      } else {
        setNewRowAuto(updates);
      }
      setPsPendingFile(file);
    } catch (err) {
      setPsExtractError(`${file.name}: ${err?.message || 'extract failed'}`);
    }
    setPsExtracting(false);
  }

  // Pre-fill the Add Incoming modal so a user can log another shipment
  // (PS log) under an existing PO without re-typing the PO ref / supplier.
  // SKU stays blank — the new shipment may be a different SKU on the
  // same PO, or the same SKU split across multiple packing slips.
  function openAddForPo(poRef, supplier) {
    setNewRow({
      sku_id: '', qty: '', expected_date: '',
      supplier: supplier || '',
      po_ref: poRef || '',
      batch_number: '', expiry_date: '', warehouse_location: '', skutopia_transfer_id: '',
      packing_slip_number: '', packing_slip_qty: '',
    });
    setPsPendingFile(null); setPsExtractError(''); setPsAutoFilled({});
    setAddOpen(true);
  }
  function closeAddModal() {
    setAddOpen(false);
    setPsPendingFile(null); setPsExtractError(''); setPsAutoFilled({});
  }

  async function saveNewRow() {
    if (!newRow.sku_id || !newRow.expected_date || !newRow.qty) return;
    // created_by is FK→access_codes.code — must be the PIN, not the human
    // name. Sending session.name silently 500'd the Lambda and the modal
    // looked frozen ("clicked Add, nothing happened" — Ace, 2026-04-28).
    try {
      await authWrite(session, 'forecaster_incoming_stock', 'insert', {
        sku_id: newRow.sku_id, expected_date: newRow.expected_date,
        qty: parseInt(newRow.qty), po_ref: newRow.po_ref?.trim() || null,
        // Migration 101 additions — Ace's "batch + expiry + warehouse + transfer ID" ask.
        batch_number: newRow.batch_number?.trim() || null,
        expiry_date: newRow.expiry_date || null,
        warehouse_location: newRow.warehouse_location?.trim() || null,
        skutopia_transfer_id: newRow.skutopia_transfer_id?.trim() || null,
        // Migration 108 additions — Ace's PO Tracker parity for Stock-Incoming.
        // packing_slip_number surfaces in the row meta line; packing_slip_qty
        // feeds the future PS-vs-Delivered cross-check (delivered_qty
        // is captured separately when the row is marked Received).
        packing_slip_number: newRow.packing_slip_number?.trim() || null,
        packing_slip_qty: newRow.packing_slip_qty ? parseInt(newRow.packing_slip_qty) : null,
        notes: null, created_by: session.code,
      });
      // Audit trail for new incoming rows. authWrite already logs to
      // auth_audit_log (HTTP-level); forecaster_activity is the
      // domain-level mirror the team actually reads in the History
      // tab. Same pattern as stockWrites.js / Demand.jsx.
      const sku = skus.find(s => s.id === newRow.sku_id);
      await aws.from('forecaster_activity').insert([{
        actor_name: session?.name,
        action: 'Incoming added',
        detail: `${sku?.sku_name || newRow.sku_id}: ${parseInt(newRow.qty)} units, ETA ${newRow.expected_date}${newRow.po_ref ? ', PO ' + newRow.po_ref : ''}${newRow.skutopia_transfer_id ? ', TR ' + newRow.skutopia_transfer_id : ''}`,
      }]);
      setNewRow({
        sku_id: '', qty: '', expected_date: '', supplier: '', po_ref: '',
        batch_number: '', expiry_date: '', warehouse_location: '', skutopia_transfer_id: '',
        packing_slip_number: '', packing_slip_qty: '',
      });
      setPsPendingFile(null); setPsExtractError(''); setPsAutoFilled({});
      setAddOpen(false);
      loadIncoming?.();
    } catch (err) {
      console.error('[IncomingTab] saveNewRow failed', err);
      const msg = err?.message || 'unknown error';
      setToast(`Add failed: ${msg}`);
      setTimeout(() => setToast(null), 5000);
    }
  }

  // Mark as disputed — Ace's "Dispute" action. Frontend captures a
  // reason and timestamps it; nothing else changes (qty / delivered_qty
  // stay as-is) so a follow-up resolve can be a simple update later.
  async function disputeRow(r) {
    const reason = window.prompt(`Dispute this receipt for ${r._sku?.sku_name || 'this SKU'}?\n\nWhy:`);
    if (reason == null) return;
    const trimmed = reason.trim();
    if (!trimmed) return;
    try {
      await authWrite(session, 'forecaster_incoming_stock', 'update',
        { dispute_reason: trimmed, disputed_at: new Date().toISOString() },
        { column: 'id', value: r.id });
      // Audit trail. forecaster_activity is bare aws.from() per the
      // pattern in stockWrites.js — it IS the audit log, so routing
      // through authWrite would double-log. Pin SKU + PO ref so the
      // entry is greppable later.
      await aws.from('forecaster_activity').insert([{
        actor_name: session?.name,
        action: 'Incoming disputed',
        detail: `${r._sku?.sku_name || r.sku_id}${r.po_ref ? ' (PO ' + r.po_ref + ')' : ''}: ${trimmed}`,
      }]);
      setToast('Receipt flagged as disputed.');
      setTimeout(() => setToast(null), 3000);
      loadIncoming?.();
    } catch (err) {
      console.error('[IncomingTab] dispute failed', err);
      alert('Dispute failed: ' + (err?.message || 'unknown error'));
    }
  }

  // Clear the dispute when the issue's resolved. Same audit pattern.
  async function resolveDispute(r) {
    if (!window.confirm('Clear the dispute on this receipt?')) return;
    try {
      await authWrite(session, 'forecaster_incoming_stock', 'update',
        { dispute_reason: null, disputed_at: null },
        { column: 'id', value: r.id });
      await aws.from('forecaster_activity').insert([{
        actor_name: session?.name,
        action: 'Incoming dispute resolved',
        detail: `${r._sku?.sku_name || r.sku_id}${r.po_ref ? ' (PO ' + r.po_ref + ')' : ''}`,
      }]);
      setToast('Dispute cleared.');
      setTimeout(() => setToast(null), 3000);
      loadIncoming?.();
    } catch (err) {
      console.error('[IncomingTab] resolve failed', err);
      alert('Resolve failed: ' + (err?.message || 'unknown error'));
    }
  }

  function handleDownload() {
    const columns = [
      { label: 'Code', width: 14, value: r => r._cat?.sku_code || '' },
      { label: 'Name', width: 30, value: r => r._sku?.sku_name || '' },
      { label: 'Supplier', width: 22, value: r => r._cat?.supplier_name || r.supplier || '' },
      { label: 'PO Ref', width: 14, value: r => r.po_ref || '' },
      { label: 'Qty (PO)', width: 12, numFmt: '#,##0', value: r => Number(r.qty || 0) },
      { label: 'Packing Slip Qty', width: 14, numFmt: '#,##0', value: r => r.packing_slip_qty != null ? Number(r.packing_slip_qty) : '' },
      { label: 'Delivered Qty', width: 14, numFmt: '#,##0', value: r => r.delivered_qty != null ? Number(r.delivered_qty) : '' },
      { label: 'Received Qty', width: 14, numFmt: '#,##0', value: r => Number(r.received_qty || 0) },
      { label: 'Expected Date', width: 14, value: r => r.expected_date || '' },
      { label: 'Batch Number', width: 16, value: r => r.batch_number || '' },
      { label: 'Expiry Date', width: 14, value: r => r.expiry_date || '' },
      { label: 'Warehouse', width: 16, value: r => r.warehouse_location || '' },
      { label: 'Skutopia Transfer ID', width: 18, value: r => r.skutopia_transfer_id || '' },
      { label: 'Status', width: 16, value: r => STATUS_PILL[computeIncomingStatus(r)]?.label || '' },
    ];
    const filename = `FON_Incoming_Stock_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Incoming Stock', columns, rows);
    logExportActivity(session, 'Incoming Stock', filename);
  }

  // Inline quick-action receipt — same write path as the new drawer.
  async function confirmReceipt() {
    if (!receiveTarget || receiving) return;
    const qty = parseInt(receiveQty) || receiveTarget.qty;
    if (qty <= 0) return;
    setReceiving(true);
    try {
      await writeReceipt(session, {
        po: receiveTarget,
        sku: receiveTarget._sku,
        qty,
        isPartial: qty < Number(receiveTarget.qty || 0),
        note: receiveComment.trim() || null,
      });
      setToast(`✓ ${qty} units received and logged. Update On-hand stock manually.`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error('[IncomingTab] inline receipt failed', err);
      alert('Receipt failed: ' + (err?.message || 'unknown error'));
    } finally {
      setReceiving(false);
      setReceiveTarget(null);
      setReceiveQty('');
      setReceiveComment('');
      loadIncoming?.();
      loadSkus?.();
    }
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 16, display: 'flex', gap: 12, padding: '0 32px', alignItems: 'center' }}>
        <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>+ Add Incoming</button>
        {/* View toggle — Mercury-style segmented control */}
        <div style={{
          display: 'inline-flex', background: '#EEECE5', border: '1px solid #E0DED6',
          borderRadius: 999, padding: 3, gap: 2,
        }} role="tablist" aria-label="Group by">
          {[
            { id: 'sku', label: 'By SKU' },
            { id: 'po',  label: 'By PO'  },
          ].map(opt => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={viewMode === opt.id}
              onClick={() => setViewMode(opt.id)}
              style={{
                padding: '4px 14px', borderRadius: 999, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                background: viewMode === opt.id ? '#0F1419' : 'transparent',
                color: viewMode === opt.id ? '#FAFAF8' : '#6E7278',
              }}
            >{opt.label}</button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowFinalized(v => !v)}
          aria-pressed={showFinalized}
          title="Show rows closed as short-received (so they can be reopened)"
          style={{
            padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            border: `1px solid ${showFinalized ? '#0F1419' : '#E0DED6'}`,
            background: showFinalized ? '#0F1419' : '#fff',
            color: showFinalized ? '#FAFAF8' : '#6E7278',
          }}
        >{showFinalized ? '✓ ' : ''}Show finalized</button>
        {canWrite && (
          <div style={{ marginLeft: 'auto' }}>
            <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>
          </div>
        )}
      </div>

      {/* Two-layer card list. Replaces the legacy 12-column table —
          same data, same actions, restructured for scan-ability.
            Top line:   SKU • status pill • qty • ETA • [Received]
            Bottom line: supplier · batch/exp · warehouse · PO/TR/PS · [Dispute]
          Multi-PO same-SKU groups stay grouped via a thin left rule
          on continuation rows, mirroring the old parent/child idea
          without a separate column. */}
      <style>{`
        .ix-row { transition: background 120ms; }
        .ix-row:hover { background: rgba(15,20,25,0.025); }
        .ix-row + .ix-row.is-child { margin-top: -1px; }
      `}</style>
      <div style={{ padding: '0 32px' }}>
        <div style={{
          display: 'flex', flexDirection: 'column',
          background: '#fff',
          border: '1px solid #E8E6E0',
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          {upcomingRows.length === 0 && (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: '#9A9DA1', fontSize: 13 }}>
              No incoming stock.
            </div>
          )}
          {viewMode === 'sku' && groups.map(g => g.list.map((r, idx) => {
            const isChild  = g.list.length > 1 && idx > 0;
            const isParent = g.list.length > 1 && idx === 0;
            const status   = computeIncomingStatus(r);
            const pill     = STATUS_PILL[status];
            const eta      = fmtDate(r.expected_date);
            const days     = r._daysTo;
            const daysText = days == null ? null : days < 0 ? `${Math.abs(days)}d late` : days === 0 ? 'today' : `${days}d`;
            const daysColor = days == null ? '#9A9DA1' : days < 0 ? '#C2410C' : days <= 7 ? '#0F8E6F' : '#6E7278';
            const meta = [
              r._cat?.supplier_name || null,
              (r.batch_number || r.expiry_date) ? `Batch: ${r.batch_number || 'TBD'}${r.expiry_date ? ` · Exp: ${fmtDate(r.expiry_date)}` : ''}` : null,
              r.warehouse_location ? `Warehouse: ${r.warehouse_location}` : null,
              [
                r.po_ref ? `PO: ${r.po_ref}` : null,
                r.skutopia_transfer_id ? `TR: ${r.skutopia_transfer_id}` : null,
                r.packing_slip_number ? `PS: ${r.packing_slip_number}` : null,
              ].filter(Boolean).join(' · ') || null,
              r.notes ? `Notes: ${r.notes}` : null,
            ].filter(Boolean);

            return (
              <div
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => setDrawerPo(r)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDrawerPo(r); } }}
                className={`ix-row ${isChild ? 'is-child' : ''}`}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 6,
                  padding: isChild ? '14px 20px 14px 36px' : '18px 20px',
                  borderTop: idx === 0 && groups[0]?.skuId !== g.skuId ? 'none' : '1px solid rgba(15,20,25,0.06)',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {isChild && (
                  <span aria-hidden="true" style={{
                    position: 'absolute', left: 22, top: 0, bottom: 0,
                    width: 2, background: 'rgba(15,20,25,0.06)',
                  }} />
                )}

                {/* TOP — primary / decision layer */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  minWidth: 0,
                }}>
                  <div style={{
                    flex: '1 1 auto', minWidth: 0,
                    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                  }}>
                    <span style={{
                      fontSize: 14.5, fontWeight: isChild ? 500 : 600, color: '#0F1419',
                      letterSpacing: '-0.005em',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }} title={r._sku?.sku_name || ''}>
                      {isChild ? '↳ ' : ''}{r._sku?.sku_name || '—'}
                    </span>
                    {r._cat?.sku_code && (
                      <span style={{ fontSize: 11, color: '#9A9DA1', fontFamily: '\'JetBrains Mono\', ui-monospace, monospace' }}>
                        {r._cat.sku_code}
                      </span>
                    )}
                    {isParent && (
                      <span style={{
                        fontSize: 10, color: '#6E7278', fontWeight: 500,
                        padding: '1px 7px', borderRadius: 999,
                        background: 'rgba(15,20,25,0.04)',
                      }}>
                        {g.list.length} POs
                      </span>
                    )}
                    <span
                      title={status === 'disputed' && r.dispute_reason ? `Disputed — ${r.dispute_reason}` : pill.label}
                      style={{
                        display: 'inline-block',
                        padding: '2px 9px',
                        borderRadius: 999,
                        fontSize: 10.5, fontWeight: 600,
                        letterSpacing: '0.04em',
                        background: pill.bg, color: pill.fg,
                      }}
                    >{pill.label}</span>
                    {r.paid && <PaidPill />}
                  </div>

                  {/* Qty — combined PO + Delivered (PS lives in the meta line). */}
                  <div style={{
                    flex: '0 0 auto', textAlign: 'right',
                    fontSize: 12.5, color: '#6E7278', whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    <span style={{ color: '#9A9DA1' }}>Qty:</span>{' '}
                    <span style={{ color: '#0F1419', fontWeight: 600 }}>
                      {(r.qty || 0).toLocaleString()}
                    </span>
                    <span style={{ color: '#9A9DA1' }}> PO / </span>
                    <span style={{
                      color: r.delivered_qty == null ? '#9A9DA1' : '#0F1419',
                      fontWeight: r.delivered_qty == null ? 400 : 600,
                    }}>
                      {r.delivered_qty == null ? '—' : Number(r.delivered_qty).toLocaleString()}
                    </span>
                    <span style={{ color: '#9A9DA1' }}> Delivered</span>
                  </div>

                  {/* Phase 2.1: receipt progress — Received X / Y · Z remaining (same as PO view) */}
                  <div style={{ flex: '0 0 auto', textAlign: 'right', fontSize: 11.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ color: '#9A9DA1' }}>Rcv: </span>
                    <span style={{ color: Number(r.received_qty || 0) > 0 ? '#0F8E6F' : '#9A9DA1', fontWeight: 600 }}>{Number(r.received_qty || 0).toLocaleString()} / {(r.qty || 0).toLocaleString()}</span>
                    {Number(r.received_qty || 0) > 0 && Number(r.received_qty || 0) < Number(r.qty || 0) && (
                      <span style={{ color: '#92400E' }}> · {Math.max(0, Number(r.qty || 0) - Number(r.received_qty || 0)).toLocaleString()} remaining</span>
                    )}
                  </div>

                  {/* ETA — date · days */}
                  <div style={{
                    flex: '0 0 auto', textAlign: 'right',
                    fontSize: 12.5, color: '#0F1419', whiteSpace: 'nowrap',
                    minWidth: 120,
                  }}>
                    {eta}
                    {daysText && (
                      <>
                        <span style={{ color: '#D5D3CD' }}> · </span>
                        <span style={{ color: daysColor, fontVariantNumeric: 'tabular-nums' }}>{daysText}</span>
                      </>
                    )}
                  </div>

                  {/* Primary CTA — Received. Only strong button on the row. */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setReceiveTarget(r); setReceiveQty(String(r.qty)); }}
                    style={{
                      flex: '0 0 auto',
                      background: 'linear-gradient(135deg, #16a34a, #22c55e, #16a34a)', backgroundSize: '200% 200%',
                      animation: 'gradientShift 2s ease infinite', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '6px 14px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                      boxShadow: '0 1px 8px rgba(22,163,74,0.18)',
                    }}
                  >Received</button>
                </div>

                {/* BOTTOM — secondary / metadata layer. Single line,
                    truncated. All remaining fields preserved. */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  fontSize: 12, color: '#6E7278', letterSpacing: '-0.005em',
                  minWidth: 0,
                }}>
                  <span style={{
                    flex: '1 1 auto', minWidth: 0,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={meta.join(' · ')}>
                    {meta.length === 0 ? <span style={{ color: '#9A9DA1' }}>No metadata yet — click row to fill in details.</span> : meta.join(' · ')}
                  </span>
                  {r.disputed_at ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); resolveDispute(r); }}
                      title={r.dispute_reason ? `Disputed — ${r.dispute_reason}\n\nClick to clear.` : 'Click to clear'}
                      style={{
                        flex: '0 0 auto',
                        background: '#EFE6F8', color: '#6B4E8C',
                        border: '1px solid #C8A6FF',
                        borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer',
                      }}
                    >Resolve</button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); disputeRow(r); }}
                      title="Flag this receipt for review"
                      style={{
                        flex: '0 0 auto',
                        background: 'transparent', color: '#9A9DA1',
                        border: '1px solid transparent',
                        borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#6E7278'; e.currentTarget.style.borderColor = 'rgba(110,114,120,0.3)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#9A9DA1'; e.currentTarget.style.borderColor = 'transparent'; }}
                    >Dispute</button>
                  )}
                </div>
              </div>
            );
          }))}

          {/* PO mode — Finance PO Tracker parity. Each PO group has a
              header strip with PO Qty / PS Qty / Delivered / Outstanding,
              a "+ Log another shipment" action, and the underlying row
              list condensed to one line per shipment for table-style
              cross-check (Ace's audit, 2026-04-29). */}
          {viewMode === 'po' && poGroups.map(pg => {
            const pct = pg.pctDelivered;
            const headerLabel = pg.poRef || 'No PO';
            return (
              <div key={pg.poRef || '__no_po__'} style={{ borderTop: '1px solid rgba(15,20,25,0.06)' }}>
                {/* Header strip — rolled-up qty stats */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '14px 20px',
                  background: 'linear-gradient(to right, #FAF9F4, #FFFFFF)',
                  borderBottom: '1px solid rgba(15,20,25,0.04)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flex: '1 1 auto', minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0F1419', fontFamily: '\'JetBrains Mono\', ui-monospace, monospace' }}>{headerLabel}</span>
                    {pg.supplier && (
                      <span style={{ fontSize: 12, color: '#6E7278', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {pg.supplier}</span>
                    )}
                    <span style={{ fontSize: 11, color: '#9A9DA1' }}>· {pg.list.length} shipment{pg.list.length === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    fontSize: 12, color: '#6E7278', whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    <span><span style={{ color: '#9A9DA1' }}>PO Qty:</span> <strong style={{ color: '#0F1419' }}>{pg.totalQty.toLocaleString()}</strong></span>
                    <span><span style={{ color: '#9A9DA1' }}>PS:</span> <strong style={{ color: '#0F1419' }}>{pg.totalPs.toLocaleString()}</strong></span>
                    <span><span style={{ color: '#9A9DA1' }}>Delivered:</span> <strong style={{ color: '#0F1419' }}>{pg.totalDelivered.toLocaleString()}</strong></span>
                    <span><span style={{ color: '#9A9DA1' }}>Outstanding:</span> <strong style={{ color: pg.outstanding > 0 ? '#C2410C' : '#0F8E6F' }}>{pg.outstanding.toLocaleString()}</strong></span>
                    <span style={{
                      padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                      background: pct === 100 ? '#E2F4EA' : pct > 0 ? '#F1F1ED' : '#FEE5DA',
                      color: pct === 100 ? '#036E43' : pct > 0 ? '#6E7278' : '#9A4820',
                    }}>{pct}%</span>
                  </div>
                  {pg.poRef && (
                    <button
                      onClick={() => openAddForPo(pg.poRef, pg.supplier)}
                      style={{
                        flex: '0 0 auto', padding: '5px 12px', borderRadius: 8,
                        background: '#0F1419', color: '#FAFAF8', border: 'none',
                        fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                      }}
                    >+ Log shipment</button>
                  )}
                </div>

                {/* Shipment rows — condensed one-line view per shipment */}
                {pg.list.map(r => {
                  const status = computeIncomingStatus(r);
                  const pill = STATUS_PILL[status];
                  const eta = fmtDate(r.expected_date);
                  const days = r._daysTo;
                  const daysText = days == null ? null : days < 0 ? `${Math.abs(days)}d late` : days === 0 ? 'today' : `${days}d`;
                  const daysColor = days == null ? '#9A9DA1' : days < 0 ? '#C2410C' : days <= 7 ? '#0F8E6F' : '#6E7278';
                  return (
                    <div
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDrawerPo(r)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDrawerPo(r); } }}
                      className="ix-row"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 20px 12px 36px',
                        borderTop: '1px solid rgba(15,20,25,0.04)',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontSize: 13.5, fontWeight: 500, color: '#0F1419', flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r._sku?.sku_name || ''}>
                        {r._sku?.sku_name || '—'}
                        {r._cat?.sku_code && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: '#9A9DA1', fontFamily: '\'JetBrains Mono\', ui-monospace, monospace' }}>{r._cat.sku_code}</span>
                        )}
                      </span>
                      <span style={{
                        padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em',
                        background: pill.bg, color: pill.fg,
                      }}>{pill.label}</span>
                      {r.paid && <PaidPill />}
                      <span style={{ fontSize: 12.5, color: '#6E7278', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', minWidth: 200, textAlign: 'right' }}>
                        <span style={{ color: '#9A9DA1' }}>PO:</span> <strong style={{ color: '#0F1419' }}>{(r.qty || 0).toLocaleString()}</strong>
                        <span style={{ color: '#D5D3CD' }}> · </span>
                        <span style={{ color: '#9A9DA1' }}>PS:</span> <strong style={{ color: r.packing_slip_qty ? '#0F1419' : '#9A9DA1' }}>{r.packing_slip_qty ? Number(r.packing_slip_qty).toLocaleString() : '—'}</strong>
                        <span style={{ color: '#D5D3CD' }}> · </span>
                        <span style={{ color: '#9A9DA1' }}>Got:</span> <strong style={{ color: r.delivered_qty == null ? '#9A9DA1' : '#0F1419' }}>{r.delivered_qty == null ? '—' : Number(r.delivered_qty).toLocaleString()}</strong>
                        <span style={{ color: '#D5D3CD' }}> · </span>
                        <span style={{ color: '#9A9DA1' }}>Rcv:</span> <strong style={{ color: Number(r.received_qty || 0) > 0 ? '#0F8E6F' : '#9A9DA1' }}>{Number(r.received_qty || 0).toLocaleString()}/{(r.qty || 0).toLocaleString()}</strong>
                        {Number(r.received_qty || 0) > 0 && Number(r.received_qty || 0) < Number(r.qty || 0) && (
                          <span style={{ color: '#92400E' }}> · {Math.max(0, Number(r.qty || 0) - Number(r.received_qty || 0)).toLocaleString()} left</span>
                        )}
                      </span>
                      <span style={{ fontSize: 12, color: '#0F1419', whiteSpace: 'nowrap', minWidth: 110, textAlign: 'right' }}>
                        {eta}
                        {daysText && (<><span style={{ color: '#D5D3CD' }}> · </span><span style={{ color: daysColor, fontVariantNumeric: 'tabular-nums' }}>{daysText}</span></>)}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setReceiveTarget(r); setReceiveQty(String(r.qty)); }}
                        style={{
                          flex: '0 0 auto',
                          background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff',
                          border: 'none', borderRadius: 8, padding: '5px 12px',
                          fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        }}
                      >Received</button>
                      {r.disputed_at ? (
                        <button onClick={(e) => { e.stopPropagation(); resolveDispute(r); }} style={{ flex: '0 0 auto', background: '#EFE6F8', color: '#6B4E8C', border: '1px solid #C8A6FF', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}>Resolve</button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); disputeRow(r); }} style={{ flex: '0 0 auto', background: 'transparent', color: '#9A9DA1', border: '1px solid transparent', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}>Dispute</button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Previous Incoming — collapsed by default */}
      {previousRows.length > 0 && (
        <div style={{ padding: '16px 32px 0' }}>
          <button
            type="button"
            onClick={() => setPrevOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, color: '#6E7278', padding: '8px 0',
            }}
          >
            <span style={{ fontSize: 11, transition: 'transform 120ms', display: 'inline-block', transform: prevOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            Previous Incoming
            <span style={{ fontSize: 11, fontWeight: 500, color: '#9A9DA1', background: '#F1F1ED', borderRadius: 999, padding: '1px 8px' }}>{previousRows.length}</span>
          </button>
          {prevOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #E8E6E0', borderRadius: 12, overflow: 'hidden', marginTop: 8 }}>
              {viewMode === 'sku' && prevGroups.map(g => g.list.map((r, idx) => {
                const isChild  = g.list.length > 1 && idx > 0;
                const isParent = g.list.length > 1 && idx === 0;
                const status   = computeIncomingStatus(r);
                const pill     = STATUS_PILL[status];
                const eta      = fmtDate(r.expected_date);
                const days     = r._daysTo;
                const daysText = days == null ? null : days < 0 ? `${Math.abs(days)}d late` : days === 0 ? 'today' : `${days}d`;
                const daysColor = days == null ? '#9A9DA1' : days < 0 ? '#C2410C' : days <= 7 ? '#0F8E6F' : '#6E7278';
                const meta = [
                  r._cat?.supplier_name || null,
                  (r.batch_number || r.expiry_date) ? `Batch: ${r.batch_number || 'TBD'}${r.expiry_date ? ` · Exp: ${fmtDate(r.expiry_date)}` : ''}` : null,
                  r.warehouse_location ? `Warehouse: ${r.warehouse_location}` : null,
                  [r.po_ref ? `PO: ${r.po_ref}` : null, r.skutopia_transfer_id ? `TR: ${r.skutopia_transfer_id}` : null, r.packing_slip_number ? `PS: ${r.packing_slip_number}` : null].filter(Boolean).join(' · ') || null,
                  r.notes ? `Notes: ${r.notes}` : null,
                ].filter(Boolean);
                return (
                  <div
                    key={r.id} role="button" tabIndex={0}
                    onClick={() => setDrawerPo(r)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDrawerPo(r); } }}
                    className={`ix-row ${isChild ? 'is-child' : ''}`}
                    style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: isChild ? '14px 20px 14px 36px' : '18px 20px', borderTop: idx === 0 && prevGroups[0]?.skuId !== g.skuId ? 'none' : '1px solid rgba(15,20,25,0.06)', cursor: 'pointer', position: 'relative', opacity: 0.75 }}
                  >
                    {isChild && <span aria-hidden="true" style={{ position: 'absolute', left: 22, top: 0, bottom: 0, width: 2, background: 'rgba(15,20,25,0.06)' }} />}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14.5, fontWeight: isChild ? 500 : 600, color: '#0F1419', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }} title={r._sku?.sku_name || ''}>{isChild ? '↳ ' : ''}{r._sku?.sku_name || '—'}</span>
                        {r._cat?.sku_code && <span style={{ fontSize: 11, color: '#9A9DA1', fontFamily: '\'JetBrains Mono\', ui-monospace, monospace' }}>{r._cat.sku_code}</span>}
                        {isParent && <span style={{ fontSize: 10, color: '#6E7278', fontWeight: 500, padding: '1px 7px', borderRadius: 999, background: 'rgba(15,20,25,0.04)' }}>{g.list.length} POs</span>}
                        <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', background: pill.bg, color: pill.fg }}>{pill.label}</span>
                        {r.paid && <PaidPill />}
                      </div>
                      <div style={{ flex: '0 0 auto', textAlign: 'right', fontSize: 12.5, color: '#6E7278', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ color: '#9A9DA1' }}>Qty:</span> <span style={{ color: '#0F1419', fontWeight: 600 }}>{(r.qty || 0).toLocaleString()}</span>
                        <span style={{ color: '#9A9DA1' }}> PO / </span>
                        <span style={{ color: r.delivered_qty == null ? '#9A9DA1' : '#0F1419', fontWeight: r.delivered_qty == null ? 400 : 600 }}>{r.delivered_qty == null ? '—' : Number(r.delivered_qty).toLocaleString()}</span>
                        <span style={{ color: '#9A9DA1' }}> Delivered</span>
                      </div>
                      <div style={{ flex: '0 0 auto', textAlign: 'right', fontSize: 11.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ color: '#9A9DA1' }}>Rcv: </span>
                        <span style={{ color: Number(r.received_qty || 0) > 0 ? '#0F8E6F' : '#9A9DA1', fontWeight: 600 }}>{Number(r.received_qty || 0).toLocaleString()} / {(r.qty || 0).toLocaleString()}</span>
                      </div>
                      <div style={{ flex: '0 0 auto', textAlign: 'right', fontSize: 12.5, color: '#0F1419', whiteSpace: 'nowrap', minWidth: 120 }}>
                        {eta}{daysText && <><span style={{ color: '#D5D3CD' }}> · </span><span style={{ color: daysColor, fontVariantNumeric: 'tabular-nums' }}>{daysText}</span></>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setReceiveTarget(r); setReceiveQty(String(r.qty)); }} style={{ flex: '0 0 auto', background: 'linear-gradient(135deg, #16a34a, #22c55e, #16a34a)', backgroundSize: '200% 200%', animation: 'gradientShift 2s ease infinite', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 8px rgba(22,163,74,0.18)' }}>Received</button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#6E7278', letterSpacing: '-0.005em', minWidth: 0 }}>
                      <span style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={meta.join(' · ')}>
                        {meta.length === 0 ? <span style={{ color: '#9A9DA1' }}>No metadata yet — click row to fill in details.</span> : meta.join(' · ')}
                      </span>
                      {r.disputed_at ? (
                        <button onClick={(e) => { e.stopPropagation(); resolveDispute(r); }} style={{ flex: '0 0 auto', background: '#EFE6F8', color: '#6B4E8C', border: '1px solid #C8A6FF', borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}>Resolve</button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); disputeRow(r); }} style={{ flex: '0 0 auto', background: 'transparent', color: '#9A9DA1', border: '1px solid transparent', borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}>Dispute</button>
                      )}
                    </div>
                  </div>
                );
              }))}
              {viewMode === 'po' && prevPoGroups.map(pg => {
                const pct = pg.pctDelivered;
                return (
                  <div key={pg.poRef || '__no_po__'} style={{ borderTop: '1px solid rgba(15,20,25,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', background: 'linear-gradient(to right, #FAF9F4, #FFFFFF)', borderBottom: '1px solid rgba(15,20,25,0.04)' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flex: '1 1 auto', minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0F1419', fontFamily: '\'JetBrains Mono\', ui-monospace, monospace' }}>{pg.poRef || 'No PO'}</span>
                        {pg.supplier && <span style={{ fontSize: 12, color: '#6E7278' }}>· {pg.supplier}</span>}
                        <span style={{ fontSize: 11, color: '#9A9DA1' }}>· {pg.list.length} shipment{pg.list.length === 1 ? '' : 's'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: '#6E7278', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        <span><span style={{ color: '#9A9DA1' }}>PO Qty:</span> <strong style={{ color: '#0F1419' }}>{pg.totalQty.toLocaleString()}</strong></span>
                        <span><span style={{ color: '#9A9DA1' }}>Delivered:</span> <strong style={{ color: '#0F1419' }}>{pg.totalDelivered.toLocaleString()}</strong></span>
                        <span><span style={{ color: '#9A9DA1' }}>Outstanding:</span> <strong style={{ color: pg.outstanding > 0 ? '#C2410C' : '#0F8E6F' }}>{pg.outstanding.toLocaleString()}</strong></span>
                        <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: pct === 100 ? '#E2F4EA' : pct > 0 ? '#F1F1ED' : '#FEE5DA', color: pct === 100 ? '#036E43' : pct > 0 ? '#6E7278' : '#9A4820' }}>{pct}%</span>
                      </div>
                    </div>
                    {pg.list.map(r => {
                      const status = computeIncomingStatus(r);
                      const pill = STATUS_PILL[status];
                      const eta = fmtDate(r.expected_date);
                      const days = r._daysTo;
                      const daysText = days == null ? null : days < 0 ? `${Math.abs(days)}d late` : days === 0 ? 'today' : `${days}d`;
                      const daysColor = days == null ? '#9A9DA1' : days < 0 ? '#C2410C' : days <= 7 ? '#0F8E6F' : '#6E7278';
                      return (
                        <div key={r.id} role="button" tabIndex={0} onClick={() => setDrawerPo(r)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDrawerPo(r); } }} className="ix-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px 12px 36px', borderTop: '1px solid rgba(15,20,25,0.04)', cursor: 'pointer', opacity: 0.75 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 500, color: '#0F1419', flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r._sku?.sku_name || '—'}{r._cat?.sku_code && <span style={{ marginLeft: 8, fontSize: 11, color: '#9A9DA1', fontFamily: '\'JetBrains Mono\', ui-monospace, monospace' }}>{r._cat.sku_code}</span>}</span>
                          <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', background: pill.bg, color: pill.fg }}>{pill.label}</span>
                          {r.paid && <PaidPill />}
                          <span style={{ fontSize: 12.5, color: '#6E7278', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', minWidth: 200, textAlign: 'right' }}>
                            <span style={{ color: '#9A9DA1' }}>PO:</span> <strong style={{ color: '#0F1419' }}>{(r.qty || 0).toLocaleString()}</strong>
                            <span style={{ color: '#D5D3CD' }}> · </span>
                            <span style={{ color: '#9A9DA1' }}>Got:</span> <strong style={{ color: r.delivered_qty == null ? '#9A9DA1' : '#0F1419' }}>{r.delivered_qty == null ? '—' : Number(r.delivered_qty).toLocaleString()}</strong>
                            <span style={{ color: '#D5D3CD' }}> · </span>
                            <span style={{ color: '#9A9DA1' }}>Rcv:</span> <strong style={{ color: Number(r.received_qty || 0) > 0 ? '#0F8E6F' : '#9A9DA1' }}>{Number(r.received_qty || 0).toLocaleString()}/{(r.qty || 0).toLocaleString()}</strong>
                          </span>
                          <span style={{ fontSize: 12, color: '#0F1419', whiteSpace: 'nowrap', minWidth: 110, textAlign: 'right' }}>
                            {eta}{daysText && <><span style={{ color: '#D5D3CD' }}> · </span><span style={{ color: daysColor, fontVariantNumeric: 'tabular-nums' }}>{daysText}</span></>}
                          </span>
                          <button onClick={(e) => { e.stopPropagation(); setReceiveTarget(r); setReceiveQty(String(r.qty)); }} style={{ flex: '0 0 auto', background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Received</button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 300, background: '#16a34a', color: '#fff', padding: '10px 20px', borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 20px rgba(22,163,74,0.3)' }}>
          {toast}
        </div>
      )}

      {/* New IncomingDrawer — opened by row click */}
      {drawerPo && (
        <IncomingDrawer
          session={session}
          po={drawerPo}
          sku={drawerPo._sku}
          catalog={catalog}
          onClose={() => setDrawerPo(null)}
          onSaved={() => { loadIncoming?.(); loadSkus?.(); }}
        />
      )}

      {/* Add Incoming popup (existing) */}
      {addOpen && (<>
        <div onClick={closeAddModal} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.2)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 201, width: 480, maxWidth: '90vw', maxHeight: '92vh', overflowY: 'auto', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 20, boxShadow: '0 8px 40px rgba(0,0,0,0.12)', padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Add Incoming Stock</div>
            <button onClick={closeAddModal} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-disabled)' }}>×</button>
          </div>

          {/* Packing-slip auto-fill — drop a PDF/image, Claude extracts
              SKU + qty + PO + TR + batch + expiry + dates and pre-fills
              the form. Auto-filled fields show a subtle highlight so users
              can spot what AI guessed vs typed. */}
          <div
            onClick={() => !psExtracting && psFileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              const f = e.dataTransfer?.files?.[0];
              if (f) extractPackingSlip(f);
            }}
            style={{
              marginBottom: 14, padding: '12px 14px', borderRadius: 10,
              border: `1.5px dashed ${psExtracting ? '#0F8E6F' : 'rgba(15,20,25,0.18)'}`,
              background: psExtracting ? 'rgba(15,142,111,0.06)' : 'rgba(15,20,25,0.02)',
              cursor: psExtracting ? 'progress' : 'pointer',
              fontSize: 12.5, color: '#6E7278', textAlign: 'center',
              transition: 'border-color 120ms, background 120ms',
            }}
          >
            {psExtracting ? (
              <span style={{ color: '#0F8E6F', fontWeight: 500 }}>Reading packing slip…</span>
            ) : psPendingFile ? (
              <span>Auto-filled from <strong style={{ color: '#0F1419' }}>{psPendingFile.name}</strong> · click to replace</span>
            ) : (
              <span>📄 Drop packing slip to auto-fill <span style={{ color: '#9A9DA1' }}>(PDF, PNG, JPG)</span></span>
            )}
          </div>
          <input
            ref={psFileRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            hidden
            onChange={e => { const f = e.target.files?.[0]; if (f) extractPackingSlip(f); e.target.value = ''; }}
          />
          {psExtractError && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#FEE5DA', color: '#9A4820', fontSize: 12 }}>
              {psExtractError}
            </div>
          )}

          <div className="form-group"><label className="form-label">SKU</label>
            <select className="form-select" value={newRow.sku_id} onChange={e => onSkuSelect(e.target.value)}>
              <option value="">Select SKU...</option>
              {catalog.filter(c => c.status === 'active').map(c => {
                const sku = skus.find(s => s.catalog_id === c.id);
                return <option key={c.id} value={sku?.id || ''}>{c.sku_code ? `${c.sku_code} · ` : ''}{c.sku_name}</option>;
              })}
            </select></div>
          <div className="row-2">
            <div className="form-group"><label className="form-label">Qty</label>
              <input className="form-input" type="number" value={newRow.qty} onChange={e => setNewRow(f => ({ ...f, qty: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">ETA</label>
              <input className="form-input" type="date" value={newRow.expected_date} onChange={e => setNewRow(f => ({ ...f, expected_date: e.target.value }))} /></div>
          </div>
          <div className="row-2">
            <div className="form-group"><label className="form-label">Supplier</label>
              <input className="form-input" value={newRow.supplier} onChange={e => setNewRow(f => ({ ...f, supplier: e.target.value }))} placeholder="Auto-filled from catalogue" /></div>
            <div className="form-group"><label className="form-label">PO Ref</label>
              <input className="form-input" value={newRow.po_ref} onChange={e => setNewRow(f => ({ ...f, po_ref: e.target.value }))} placeholder="Optional" /></div>
          </div>
          <div className="row-2">
            <div className="form-group"><label className="form-label">Batch Number</label>
              <input className="form-input" value={newRow.batch_number} onChange={e => setNewRow(f => ({ ...f, batch_number: e.target.value }))} placeholder="e.g. LOT-240901-A" /></div>
            <div className="form-group"><label className="form-label">Expiry Date</label>
              <input className="form-input" type="date" value={newRow.expiry_date} onChange={e => setNewRow(f => ({ ...f, expiry_date: e.target.value }))} /></div>
          </div>
          <div className="row-2">
            <div className="form-group"><label className="form-label">Warehouse</label>
              <select className="form-select" value={newRow.warehouse_location} onChange={e => setNewRow(f => ({ ...f, warehouse_location: e.target.value }))}>
                <option value="">— Pick a warehouse —</option>
                <option value="Sydney HQ">Sydney HQ</option>
                <option value="Skutopia">Skutopia</option>
                <option value="Amazon FBA AU">Amazon FBA AU</option>
                <option value="Amazon FBA US">Amazon FBA US</option>
                <option value="Netherlands 3PL">Netherlands 3PL</option>
                <option value="In transit">In transit</option>
                <option value="Other">Other</option>
              </select></div>
            <div className="form-group"><label className="form-label">Skutopia Transfer ID</label>
              <input className="form-input" value={newRow.skutopia_transfer_id} onChange={e => setNewRow(f => ({ ...f, skutopia_transfer_id: e.target.value }))} placeholder="Optional · for cross-checking" /></div>
          </div>
          <div className="row-2">
            <div className="form-group"><label className="form-label">Packing Slip #</label>
              <input className="form-input" value={newRow.packing_slip_number} onChange={e => setNewRow(f => ({ ...f, packing_slip_number: e.target.value }))} placeholder="Optional · from packing slip" /></div>
            <div className="form-group"><label className="form-label">Packing Slip Qty</label>
              <input className="form-input" type="number" value={newRow.packing_slip_qty} onChange={e => setNewRow(f => ({ ...f, packing_slip_qty: e.target.value }))} placeholder="Qty per packing slip" /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={saveNewRow} disabled={!newRow.sku_id || !newRow.qty || !newRow.expected_date}>Add</button>
            <button className="btn btn-secondary" onClick={closeAddModal}>Cancel</button>
          </div>
        </div>
      </>)}

      {/* Inline "Mark as Received" quick popup (existing — now routes
          through writeReceipt for unified ledger writes) */}
      {receiveTarget && (<>
        <div onClick={() => setReceiveTarget(null)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.2)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 201, width: 400, maxWidth: '90vw', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 20, boxShadow: '0 8px 40px rgba(0,0,0,0.12)', padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Mark as Received</div>
            <button onClick={() => setReceiveTarget(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-disabled)' }}>×</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-subdued)', marginBottom: 12 }}>{receiveTarget._sku?.sku_name} · {receiveTarget.qty?.toLocaleString()}u expected</div>
          <div className="form-group"><label className="form-label">Qty Received</label>
            <input className="form-input" type="number" value={receiveQty} onChange={e => setReceiveQty(e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Comment</label>
            <textarea className="form-textarea" rows={2} placeholder="Packing slip notes, condition, discrepancies... (optional)" value={receiveComment} onChange={e => setReceiveComment(e.target.value)} /></div>
          <div style={{ fontSize: 11, color: 'var(--text-disabled)', marginTop: 4, marginBottom: 8 }}>
            Attachments → open the drawer to upload by type (packing slip / POD / invoice / photo).
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={confirmReceipt} disabled={receiving} style={{
              flex: 1, background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff', border: 'none',
              borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(22,163,74,0.3)',
            }}>{receiving ? 'Processing...' : 'Confirm Receipt'}</button>
            <button className="btn btn-secondary" onClick={() => setReceiveTarget(null)}>Cancel</button>
          </div>
        </div>
      </>)}
    </>
  );
}
